/* 
Copyright (c) Karsten Jeske and John Firth
This source code is licensed under the proprietary license found in the
LICENSE file in the root directory of this source tree.
*/

import DisclaimerFooter from "components/DisclaimerFooter"
import { useDataInfo } from "hooks/useDataInfo"
import { useEffect } from "react"
import { Link } from "react-router"
import { countSimulationCohorts } from "utils/dateUtils"
import { pageMetaData, updateMetaTags } from "utils/metaTags"

const Methodology = () => {
  const { dataInfo, loading, error } = useDataInfo()
  const cohortCount =
    loading || error || !dataInfo?.maxSimulationStartYear
      ? null
      : countSimulationCohorts(dataInfo.earliestDataDate, dataInfo.maxSimulationStartYear)

  useEffect(() => {
    const { title, description, keywords } = pageMetaData.methodology
    updateMetaTags(title, description, keywords)
  }, [])

  return (
    <div className="pt-1 pb-3">
      <h2 className="fs-4 mb-3">Foundation</h2>
      <p>
        Safe Retirement Spending is based on the calculational methodology used extensively by
        Karsten Jeske (<em>Early&nbsp;Retirement&nbsp;Now</em>) in his{" "}
        <a
          href="
        https://earlyretirementnow.com/safe-withdrawal-rate-series/"
        >
          Safe Withdrawal Rate series
        </a>
        , which is also encapsulated in his{" "}
        <a
          href="https://earlyretirementnow.com/2018/08/29/google-sheet-updates-swr-series-part-28/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Sheet-based SWR Toolbox
        </a>
        . The math is discussed in detail here:{" "}
        <a
          href="https://earlyretirementnow.com/2017/02/01/the-ultimate-guide-to-safe-withdrawal-rates-part-8-technical-appendix/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Part 8: Technical Appendix
        </a>
        . The most relevant aspects of the methodology are discussed below.
      </p>
      <h2 id="historical-simulation" className="fs-4 mb-3">
        Historical Simulation Approach
      </h2>
      <h3 className="fs-5 mt-3 mb-2">Overview</h3>
      <p>
        Our analysis uses a historical simulation methodology that replays actual market sequences
        going back to February 1871. Rather than using Monte Carlo simulations with randomized
        returns, we preserve the real chronological order of market events to capture
        sequence-of-returns risk accurately.
      </p>
      <h3 className="fs-5 mt-3 mb-2">Details</h3>
      <p>
        The simulation process rolls forward one month at a time from February 1871 to the present,
        calculating how your retirement plan would have performed had it begun in each month.
      </p>
      <p>
        Each simulation begins with the specified initial portfolio value and asset allocation.
        Monthly portfolio returns are calculated based on the weighted allocation across all asset
        classes, adjusted for specified expense ratios.
      </p>
      <p>
        All portfolio returns and values, including the final portfolio values shown in the results,
        are in real (inflation-adjusted) terms — expressed in today&apos;s purchasing power rather
        than future nominal dollars.
      </p>
      <p>
        For glide path strategies, asset allocations are automatically adjusted each month at a
        fixed rate between initial to final allocations over the specified length of the glide path.
        (Thereafter, the final allocation is maintained.)
      </p>
      {/* AIDEV-NOTE: the cutoff is fixed, not derived from the latest data date
          (#114). Karsten keeps ERN's cohort set constant so failure rates are
          always divided by the same number of cohorts; this paragraph once
          claimed a "ten years of real data" rationale nobody holds. */}
      <p>
        Retirement simulations start no later than{" "}
        {loading || error || !dataInfo?.maxSimulationStartYear
          ? "loading"
          : `December ${dataInfo.maxSimulationStartYear}`}
        . This cutoff is fixed on purpose: every failure rate is always calculated over the same{" "}
        {cohortCount === null ? "loading" : cohortCount.toLocaleString()} retirement cohorts, so
        adding newer return data doesn&apos;t change what the rates are measured against. Results
        for the most recent cohorts can still shift slightly as real returns replace projected ones;
        see the <a href="#projected-returns">projected returns</a> section.
      </p>
      <p>
        Any cash flows are applied at your specified times timing throughout the retirement period,
        adjusted for inflation with your specified rate where applicable.
      </p>
      <h3 className="fs-5 mt-3 mb-2">Rationale</h3>
      <p>
        Preserving the chronological order lets us capture entire market cycles and the shifting
        macro regimes in which they unfolded. Using monthly, as opposed to annual, returns avoids
        over-aggregation of data from particularly eventful years (e.g. 1929), and the unrealistic
        assumption of withdrawing funds only once per year.
      </p>
      <p>
        As Karsten discusses in several posts, standard Monte-Carlo models that shuffle
        i.i.d.&nbsp;annual returns don&apos;t incorporate two key realities: (1)&nbsp;
        <em>mean&nbsp;reversion</em>&nbsp;— bad or stellar decades tend to be followed by the
        opposite (
        <a
          href="https://earlyretirementnow.com/2018/01/24/random-walk/"
          target="_blank"
          rel="noopener noreferrer"
        >
          How Much of a Random Walk is the Stock Market?
        </a>
        ); and (2)&nbsp;<em>time‑varying stock–bond correlations and valuations</em>, which drive
        withdrawal outcomes but are held constant in most MC engines (
        <a
          href="https://earlyretirementnow.com/2017/09/20/the-ultimate-guide-to-safe-withdrawal-rates-part-20-more-thoughts-on-equity-glidepaths/"
          target="_blank"
          rel="noopener noreferrer"
        >
          SWR Series Part 20
        </a>
        ). For further discussion, see the{" "}
        <a
          href="https://earlyretirementnow.com/2019/07/10/monte-carlo-plan-for-retirement-guest-post-gasem/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Guest Post on Monte Carlo Simulations
        </a>
        .
      </p>
      <h2 className="fs-4 mt-4 mb-3">Data Freshness</h2>
      <p>
        Our dataset currently covers{" "}
        {loading ? "2/1871" : error ? "2/1871" : dataInfo?.earliestDataDate} -{" "}
        {loading ? "present" : error ? "present" : dataInfo?.latestDataDate}.
      </p>

      <h2 id="data-sources" className="fs-4 mt-4 mb-3">
        Data Sources
      </h2>
      <p>
        All return series used in our analysis reflect total returns: they incorporate dividends
        from equities, coupon payments from bonds, and other distributions reinvested at the time of
        payment.
      </p>
      <p>
        If you have concerns about any of the data series used to construct the overall returns
        sequences, you can use the starting year filter in the Results page to exclude simulations
        that would use them.
      </p>
      <h3 className="fs-5 mt-3 mb-2">Stock Market Data</h3>
      <ul className="mb-3">
        <li>
          <strong>S&amp;P 500:</strong>&nbsp;
          <a
            href="http://www.econ.yale.edu/~shiller/data.htm"
            target="_blank"
            rel="noopener noreferrer"
          >
            Robert Shiller’s monthly real total-return data
          </a>
          &nbsp;(1871 – 1925) with&nbsp;
          <a
            href="https://mba.tuck.dartmouth.edu/pages/faculty/ken.french/data_library.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Fama-French U.S. Market Research Returns
          </a>
          &nbsp;(1926 – present).
        </li>
        <li>
          <strong>Non-US:</strong>&nbsp;
          <a
            href="https://www.msci.com/indexes/index/991000"
            target="_blank"
            rel="noopener noreferrer"
          >
            MSCI World ex-US index
          </a>
          . For 1871-1969 we proxy with the S&amp;P 500.
        </li>
        <li>
          <strong>Factor Analysis:</strong>&nbsp;
          <a
            href="https://mba.tuck.dartmouth.edu/pages/faculty/ken.french/data_library.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            Fama-French factor models
          </a>
          &nbsp;for small-cap and value factor weights from 07/1926-present. Prior to 07/1926 they
          are set to 0. (See the <Link to="/guide/">Guide</Link> for more details on how to use
          these factors.)
        </li>
      </ul>

      <h3 className="fs-5 mt-3 mb-2">Fixed Income Data</h3>
      <ul className="mb-3">
        <li>
          <strong>10-Year Treasury:</strong> Shiller 10-yr yields (1871-1925);&nbsp;
          <a
            href="https://rpc.cfainstitute.org/research-foundation/sbbi"
            target="_blank"
            rel="noopener noreferrer"
          >
            Ibbotson SBBI long-term gov’t bonds
          </a>
          &nbsp;(1926-1976);&nbsp;
          <a
            href="https://fred.stlouisfed.org/series/DGS10"
            target="_blank"
            rel="noopener noreferrer"
          >
            FRED DGS10 constant-maturity yields
          </a>
          &nbsp;(1977-present).
        </li>
        <li>
          <strong>30-Year Treasury:</strong>&nbsp;
          <a
            href="https://fred.stlouisfed.org/series/DGS30"
            target="_blank"
            rel="noopener noreferrer"
          >
            FRED DGS30 yields
          </a>
          . Pre-1919 values use a regression proxy (200 % 10-yr − 100 % cash).
        </li>
        <li>
          <strong>3m U.S. T-bills:</strong> Shiller 1-yr T-bills (1871-1927);&nbsp;
          <a
            href="https://pages.stern.nyu.edu/~adamodar/New_Home_Page/datafile/histretSP.html"
            target="_blank"
            rel="noopener noreferrer"
          >
            NYU-Stern annual T-bill yields
          </a>
          &nbsp;(1928-1933);&nbsp;
          <a
            href="https://fred.stlouisfed.org/series/TB3MS"
            target="_blank"
            rel="noopener noreferrer"
          >
            FRED TB3MS 3-month T-bills
          </a>
          &nbsp;(1934-present);
        </li>
      </ul>

      <h3 className="fs-5 mt-3 mb-2">Alternative Assets</h3>
      <ul className="mb-3">
        <li>
          <strong>Commodities:</strong> gold returns as commodity proxy (1871-1877);&nbsp;
          <a
            href="https://www.aqr.com/Insights/Datasets/Commodities-for-the-Long-Run-Original-Paper-Data"
            target="_blank"
            rel="noopener noreferrer"
          >
            AQR “Commodities for the Long Run” excess-return index
          </a>
          &nbsp;(1877-1970) plus cash; and&nbsp;
          <a
            href="https://www.spglobal.com/spdji/en/indices/commodities/sp-gsci/#overview"
            target="_blank"
            rel="noopener noreferrer"
          >
            S&amp;P GSCI Total Return index
          </a>
          &nbsp;(1970-present).
        </li>
        <li>
          <strong>Gold:</strong> annual prices from&nbsp;
          <a
            href="https://onlygold.com/gold-prices/historical-gold-prices/"
            target="_blank"
            rel="noopener noreferrer"
          >
            OnlyGold.com
          </a>
          (1871-1967) ;&nbsp;
          <a
            href="https://www.lbma.org.uk/prices-and-data/precious-metal-prices"
            target="_blank"
            rel="noopener noreferrer"
          >
            LBMA London Gold Fixing
          </a>
          &nbsp;(1968-present).
        </li>
      </ul>

      <h3 className="fs-5 mt-3 mb-2">Economic Indicators</h3>
      <ul className="mb-3">
        <li>
          <strong>Inflation:</strong>&nbsp;
          <a href="https://www.bls.gov/cpi/" target="_blank" rel="noopener noreferrer">
            BLS CPI-U
          </a>
          .
        </li>
        <li>
          <strong>Market Valuation:</strong> CAPE (Cyclically Adjusted Price-to-Earnings) ratios
          from{" "}
          <a
            href="http://www.econ.yale.edu/~shiller/data.htm"
            target="_blank"
            rel="noopener noreferrer"
          >
            Shiller
          </a>{" "}
          and{" "}
          <a
            href="https://earlyretirementnow.com/2022/10/05/building-a-better-cape-ratio/"
            target="_blank"
            rel="noopener noreferrer"
          >
            Early Retirement Now
          </a>{" "}
          methodologies.
        </li>
      </ul>

      <h3 id="projected-returns" className="fs-4 mt-4 mb-3">
        Projected Returns
      </h3>
      <p>
        For periods beyond our historical data (post{" "}
        {loading ? "loading" : error ? "currently available" : dataInfo?.latestDataDate}), we use
        projected returns to extend simulations through to their full retirement horizons. Because
        sequence-of-returns risk is driven almost entirely by the first 10-15&nbsp;years of a
        retirement, the precise filler values have limited impact on retirement outcomes. For
        example, Karsten demonstrated (in 2018) that boosting the projected US stock returns
        dramatically, from 3.75&nbsp;% to 20&nbsp;% p.a., shifted the 1960s cohort&apos;s fail-safe
        consumption rate by only about 0.06%&nbsp;— see&nbsp;
        <a
          href="https://earlyretirementnow.com/2018/08/29/google-sheet-updates-swr-series-part-28/"
          target="_blank"
          rel="noopener noreferrer"
        >
          SWR&nbsp;Series&nbsp;Part&nbsp;28
        </a>
        .
      </p>
      <p>
        Nevertheless, if you&apos;re uncomfortable with using projected returns, you can restrict
        the starting date range for your simulations in the Results page to exclude simulations that
        would use them.
      </p>
      <p>The future projections are divided into two periods:</p>

      <h4 id="near-term-projections" className="fs-5 mt-3 mb-2">
        Near-Term Projections (
        {loading
          ? "loading"
          : error
            ? "currently available"
            : `${dataInfo?.projectionPeriods.tenYear.startDate} - ${dataInfo?.projectionPeriods.tenYear.endDate}`}
        )
      </h4>
      <p>Based on current market conditions and near-term economic outlook:</p>
      <ul id="near-term-returns" className="mb-3">
        <li>
          <strong>S&P 500:</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.tenYear.returns.real_spx_tr ?? 0) * 100).toFixed(1)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>10-year Treasury:</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.tenYear.returns.real_10y_bm ?? 0) * 100).toFixed(2)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>30-year Treasury:</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.tenYear.returns.real_30y_bm ?? 0) * 100).toFixed(2)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>Cash (3-month T-bills):</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.tenYear.returns.real_cash ?? 0) * 100).toFixed(1)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>Non-US Equities:</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.tenYear.returns.real_world_ex_us_equities ?? 0) * 100).toFixed(1)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>Commodities:</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.tenYear.returns.real_commodities ?? 0) * 100).toFixed(1)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>Gold:</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.tenYear.returns.real_gold ?? 0) * 100).toFixed(1)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>Fama-French Small-Cap Factor (SMB):</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.tenYear.returns.fama_french_smb_raw ?? 0) * 100).toFixed(1)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>Fama-French Value Factor (HML):</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.tenYear.returns.fama_french_hml_raw ?? 0) * 100).toFixed(1)}%`}{" "}
          real annual return
        </li>
      </ul>

      <h4 id="long-term-projections" className="fs-5 mt-3 mb-2">
        Long-Term Projections (
        {loading
          ? "loading"
          : error
            ? "currently available"
            : `${dataInfo?.projectionPeriods.longTerm.startDate} - ${dataInfo?.projectionPeriods.longTerm.endDate}`}
        )
      </h4>
      <p>Based on historical long-term averages adjusted for current conditions:</p>
      <ul id="long-term-returns" className="mb-3">
        <li>
          <strong>S&P 500:</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.longTerm.returns.real_spx_tr ?? 0) * 100).toFixed(1)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>10-year Treasury:</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.longTerm.returns.real_10y_bm ?? 0) * 100).toFixed(2)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>30-year Treasury:</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.longTerm.returns.real_30y_bm ?? 0) * 100).toFixed(2)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>Cash (3-month T-bills):</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.longTerm.returns.real_cash ?? 0) * 100).toFixed(1)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>Non-US Equities:</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.longTerm.returns.real_world_ex_us_equities ?? 0) * 100).toFixed(1)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>Commodities:</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.longTerm.returns.real_commodities ?? 0) * 100).toFixed(1)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>Gold:</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.longTerm.returns.real_gold ?? 0) * 100).toFixed(1)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>Fama-French Small-Cap Factor (SMB):</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.longTerm.returns.fama_french_smb_raw ?? 0) * 100).toFixed(1)}%`}{" "}
          real annual return
        </li>
        <li>
          <strong>Fama-French Value Factor (HML):</strong>{" "}
          {loading
            ? "loading"
            : error
              ? "currently available"
              : `${((dataInfo?.projectionPeriods.longTerm.returns.fama_french_hml_raw ?? 0) * 100).toFixed(1)}%`}{" "}
          real annual return
        </li>
      </ul>

      {/* AIDEV-NOTE: #935 - this section describes what is DONE, never what is guaranteed.
          It previously promised results were validated "to ensure mathematical accuracy" and
          that checks "ensure computational integrity", which contradicted Disclaimers.tsx
          ("makes no representations of the accuracy..."). A disclaimer is not defeated by
          being weak, it is defeated by being contradicted, so keep "ensure", "accurate",
          "guarantee" and "integrity" out of any rewrite here. The cross-check itself is real
          and worth telling users; only the outcome language was the problem. Guarded by
          utils/legalCopy.test.ts. */}
      <h2 className="fs-4 mt-4 mb-3">How Results Are Checked</h2>

      <p>
        We cross-check our simulation output against the original SWR Toolbox Google Sheet mentioned
        earlier, and the calculation code is covered by an automated test suite that runs on every
        change. Both describe how the tool is built and maintained. Neither is an assurance about
        any result it produces for you, and market outcomes may differ from any projection shown
        here for reasons no amount of testing addresses.
      </p>

      <DisclaimerFooter />
    </div>
  )
}

export default Methodology
